import { useId } from "react";

type Props = {
  arcs?: boolean;
  sparkles?: boolean;
  intensity?: "default" | "soft";
};

/**
 * Decorative backdrop for dark sections — mirrors the tools hero polish:
 * radial gradient wash, conic-gradient glow, faint grid, decorative arc rings,
 * and sparkle accents. Render as the first child of a section/footer that has
 * `relative isolate overflow-hidden` and any dark base color (bg-foreground,
 * bg-ink, bg-gradient-night, etc.). All layers sit at -z-10.
 */
export function DarkSectionBackdrop({
  arcs = true,
  sparkles = true,
  intensity = "default",
}: Props) {
  const uid = useId().replace(/:/g, "");
  const arc1 = `dsbArc1-${uid}`;
  const arc2 = `dsbArc2-${uid}`;
  const mult = intensity === "soft" ? 0.45 : 0.6;
  const pct = (n: number) => Math.round(n * mult);
  return (
    <>
      {/* Darkening overlay so light text reads cleanly over the colored wash */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.55))" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background: `radial-gradient(60% 80% at 12% 18%, color-mix(in oklab, #3b82f6 ${pct(30)}%, transparent), transparent 60%), radial-gradient(55% 75% at 88% 28%, color-mix(in oklab, #7c3aed ${pct(30)}%, transparent), transparent 60%), radial-gradient(45% 60% at 50% 110%, color-mix(in oklab, #6366f1 ${pct(22)}%, transparent), transparent 60%)`,
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 -z-10 h-[460px] w-[860px] -translate-x-1/2 rounded-full opacity-[0.18] blur-3xl"
        style={{
          background:
            "conic-gradient(from 90deg, #3b82f6, #6366f1, #8b5cf6, #a855f7, #7c3aed, #4f46e5, #3b82f6)",
        }}
      />
      {arcs && (
        <>
          <svg
            className="pointer-events-none absolute -right-48 -top-48 -z-10 h-[700px] w-[700px] opacity-25 blur-[1px]"
            viewBox="0 0 700 700"
            fill="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={arc1} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0" />
                <stop offset="40%" stopColor="#6366f1" stopOpacity="0.7" />
                <stop offset="70%" stopColor="#a855f7" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
              </linearGradient>
            </defs>
            <circle cx="350" cy="350" r="340" stroke={`url(#${arc1})`} strokeWidth="1" />
            <circle
              cx="350"
              cy="350"
              r="300"
              stroke={`url(#${arc1})`}
              strokeWidth="0.75"
              opacity="0.5"
            />
          </svg>
          <svg
            className="pointer-events-none absolute -bottom-56 -left-48 -z-10 h-[520px] w-[520px] opacity-20 blur-[1px]"
            viewBox="0 0 520 520"
            fill="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={arc2} x1="0" y1="1" x2="1" y2="0">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.5" />
                <stop offset="60%" stopColor="#8b5cf6" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
              </linearGradient>
            </defs>
            <circle cx="260" cy="260" r="250" stroke={`url(#${arc2})`} strokeWidth="1" />
            <circle cx="260" cy="260" r="210" stroke={`url(#${arc2})`} strokeWidth="0.75" />
            <circle cx="260" cy="260" r="170" stroke={`url(#${arc2})`} strokeWidth="0.75" />
          </svg>
        </>
      )}
      {sparkles && (
        <>
          <span
            className="pointer-events-none absolute left-[6%] top-[18%] -z-10 text-2xl text-white/60 animate-pulse"
            aria-hidden="true"
          >
            ✦
          </span>
          <span
            className="pointer-events-none absolute left-[4%] bottom-[18%] -z-10 text-3xl text-white/70 animate-pulse [animation-delay:600ms]"
            aria-hidden="true"
          >
            ✦
          </span>
          <span
            className="pointer-events-none absolute right-[8%] top-[12%] -z-10 text-xl text-primary/70 animate-pulse [animation-delay:1200ms]"
            aria-hidden="true"
          >
            ✦
          </span>
        </>
      )}
    </>
  );
}
